/**
 * Veritabanı hatalarının penceresi.
 *
 * Saklı yordamlar hatayı `SP100_GenerateError` üzerinden fırlatıyor; metin hep
 * `Konum / Satır / Mesaj` bloğu halinde geliyor. Kullanıcıyı ilgilendiren tek satır
 * sonuncusu, ilk ikisi hatayı arayan geliştirici için.
 *
 * Bu yüzden iş mesajı önde ve okunur boyutta; kaynak bilgisi (hangi yordam, hangi
 * satır) katlanmış bir bölümde duruyor — gerektiğinde açılıyor. Tamamını panoya
 * kopyalamak için bir düğme var: destek hattına yapıştırılan şey genelde bu.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { useErrorDialogStore } from './errorDialogStore';
import { SpMessage } from './SpMessage';
import { useAuthStore } from '@/entities/session/model/authStore';

export function ErrorDialog() {
  const { t } = useTranslation();
  const error = useErrorDialogStore((s) => s.error);
  const close = useErrorDialogStore((s) => s.close);
  const userName = useAuthStore((s) => s.user?.userName ?? '');
  const [showDetail, setShowDetail] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!error) return null;
  const hasDetail = !!(error.procedure || error.line || error.code || error.extra);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(error.raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Pano yoksa (izin verilmemiş, güvensiz bağlam) sessiz kal: metin zaten ekranda.
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) {
          setShowDetail(false);
          close();
        }
      }}
      size="md"
      // Başlıkta kullanıcının adı: aynı ekranı birkaç kişi paylaşıyor ve uyarının kime
      // geldiği ilk bakışta belli olsun.
      title={userName ? t('errors.titleWithUser', { name: userName }) : t('errors.title')}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => void copy()}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? t('errors.copied') : t('errors.copy')}
          </Button>
          <Button size="sm" onClick={close}>
            {t('dialog.ok')}
          </Button>
        </div>
      }
    >
      <div className="flex gap-3">
        <span className="bg-danger/10 text-danger flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 space-y-3">
          {error.context ? <p className="text-fg-muted text-xs">{error.context}</p> : null}

          {/* İş mesajı: kullanıcının okuması gereken tek satır. */}
          <SpMessage text={error.message} className="text-fg text-sm leading-relaxed" />

          {hasDetail ? (
            <div className="border-border border-t pt-2">
              <button
                type="button"
                onClick={() => setShowDetail((v) => !v)}
                className="text-fg-muted hover:text-fg flex items-center gap-1 text-xs font-medium"
              >
                {showDetail ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                {t('errors.technical')}
              </button>

              {showDetail ? (
                <dl className="text-fg-muted mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-[11.5px]">
                  {error.procedure ? (
                    <>
                      <dt className="text-fg-subtle">{t('errors.location')}</dt>
                      <dd className="break-all">{error.procedure}</dd>
                    </>
                  ) : null}
                  {error.line ? (
                    <>
                      <dt className="text-fg-subtle">{t('errors.line')}</dt>
                      <dd>{error.line}</dd>
                    </>
                  ) : null}
                  {error.code ? (
                    <>
                      <dt className="text-fg-subtle">{t('errors.code')}</dt>
                      <dd>{error.code}</dd>
                    </>
                  ) : null}
                  {error.extra ? (
                    <>
                      <dt className="text-fg-subtle">{t('errors.other')}</dt>
                      <dd className="break-words whitespace-pre-line">{error.extra}</dd>
                    </>
                  ) : null}
                </dl>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
